/**
 * Multi-Step Form Builder
 * -----------------------
 * A reusable Alpine.js component for creating multi-step forms with validation,
 * file uploads, and persistent state.
 *
 * The form builder handles:
 * - Multiple form sections with navigation
 * - Input validation
 * - Form data persistence (using localStorage)
 * - File uploads with preview
 * - Grouped inputs (checkboxes, radio buttons)
 * - Multiple agreement checkboxes
 * - Form submission with custom handlers and endpoints
 *
 *  * Usage:
 * <form id="my-form" x-data="formHandler({
 *   formId: 'my-form',
 *   storagePrefix: 'myForm',
 *   submitEndpoint: '/custom-endpoint',
 *   agreeCheckboxId: ['terms-agree', 'privacy-agree']
 * })">
 *   <!-- Form implementation here -->
 *   <div class="form-success-message" x-cloak x-show="formSubmitted">
 *     Form submitted successfully!
 *   </div>
 * </form>
 *
 * @version 1.2.0
 */
document.addEventListener("alpine:init", () => {
  /**
   * Alpine.js formHandler component
   *
   * @param {Object} config - Configuration options for the form
   * @param {string} [config.formId='multi-page-form'] - The HTML ID of the form element
   * @param {string} [config.storagePrefix='multiStepForm'] - Prefix for localStorage keys
   * @param {string} [config.sectionSelector='.form-section'] - CSS selector for form sections
   * @param {number} [config.maxFileSize=5] - Maximum file upload size in MB
   * @param {number} [config.submitDelay=1500] - Delay for simulated submissions in ms
   * @param {string|string[]} [config.agreeCheckboxId=['agree']] - ID or array of IDs for terms agreement checkboxes
   * @param {string} [config.submitEndpoint='/do-web-action'] - Endpoint URL for form submission
   * @param {Function} [config.onSubmit] - Custom submission handler function
   * @returns {Object} Alpine.js component methods and properties
   */
  Alpine.data("formHandler", (config = {}) => ({
    // Configurable options with defaults
    formId: config.formId || "multi-page-form",
    storagePrefix: config.storagePrefix || "multiStepForm",
    sectionSelector: config.sectionSelector || ".form-section",
    maxFileSize: config.maxFileSize || 5, // in MB
    submitDelay: config.submitDelay || 1500, // in ms
    agreeCheckboxId: config.agreeCheckboxId || ["agree"],
    submitEndpoint: config.submitEndpoint || "/do-web-action", // Default endpoint for form submission

    // Form state
    sections: [], // Array of form section objects
    currentSection: null, // ID of the currently active section
    visitedSections: [], // Array of sections the user has visited
    formSubmitted: false, // Whether the form has been submitted
    isLoading: false, // Loading state during submission
    formData: {}, // Object containing all form input values
    errors: {}, // Validation errors by field name

    /**
     * Initialize the form handler
     * This runs when the Alpine component is initialized
     */
    init() {
      // Parse form sections from HTML
      this.parseSections();

      // Load saved data if available
      this.loadFormData();

      // Set up form inputs
      this.setupFormInputs();

      // Add hash change listener
      window.addEventListener("hashchange", () => this.handleHashChange());

      // Check URL hash for navigation - IMPORTANT: Do this after loading form data
      this.$nextTick(() => {
        this.handleHashChange();
      });

      // Save data when user leaves the page
      window.addEventListener("beforeunload", () => {
        this.saveFormData();
      });
    },

    /**
     * Parse form sections from HTML
     * Finds all elements matching the section selector and creates
     * a sections array with metadata
     */
    parseSections() {
      const formSections = document.querySelectorAll(this.sectionSelector);

      this.sections = Array.from(formSections).map((section) => {
        const id = section.id;
        const title = section.getAttribute("data-title") || id;

        return {
          id,
          title,
          element: section,
        };
      });

      // Set initial section
      if (this.sections.length > 0) {
        this.currentSection = this.sections[0].id;
        this.visitedSections.push(this.currentSection);
      }

      // Update section display
      this.updateSectionDisplay();
    },

    /**
     * Set up form inputs with event listeners
     * Attaches change handlers to various input types and
     * sets initial values from saved form data
     */
    setupFormInputs() {
      // Set up text inputs and selects
      const inputs = document.querySelectorAll(
        'input:not([type="file"]):not([type="checkbox"]):not([type="radio"]), select, textarea',
      );
      inputs.forEach((input) => {
        input.addEventListener("change", () => {
          this.formData[input.name] = input.value;
          this.saveFormData();
        });

        // Set initial values from formData
        if (this.formData[input.name]) {
          input.value = this.formData[input.name];
        }
      });

      // Set up individual checkboxes
      const checkboxes = document.querySelectorAll(
        'input[type="checkbox"]:not([data-group])',
      );
      checkboxes.forEach((checkbox) => {
        checkbox.addEventListener("change", () => {
          this.formData[checkbox.name] = checkbox.checked;
          this.saveFormData();
        });

        // Set initial values from formData
        if (this.formData[checkbox.name] !== undefined) {
          checkbox.checked = this.formData[checkbox.name];
        }
      });

      // Set up grouped inputs (checkboxes and radios)
      this.setupGroupedInputs();

      // Set up agree checkboxes if they exist
      if (this.agreeCheckboxExists()) {
        const agreeCheckboxes = this.getAgreeCheckboxes();

        agreeCheckboxes.forEach((checkbox) => {
          const checkboxId = checkbox.id;
          // Use checkbox ID as the key in formData
          if (this.formData[checkboxId] !== undefined) {
            checkbox.checked = this.formData[checkboxId];
          }

          // Add change event listener for each agreement checkbox
          checkbox.addEventListener("change", () => {
            this.formData[checkboxId] = checkbox.checked;
            this.saveFormData();
          });
        });
      }
    },

    /**
     * Check if any of the agreement checkboxes exist in the DOM
     *
     * @returns {boolean} True if any of the checkboxes exist
     */
    agreeCheckboxExists() {
      // Handle single string ID case
      if (typeof this.agreeCheckboxId === "string") {
        return document.getElementById(this.agreeCheckboxId) !== null;
      }

      // Handle array of IDs case
      if (Array.isArray(this.agreeCheckboxId)) {
        return this.agreeCheckboxId.some(
          (id) => document.getElementById(id) !== null,
        );
      }

      return false;
    },

    /**
     * Get all existing agreement checkboxes
     *
     * @returns {HTMLElement[]} Array of checkbox elements
     */
    getAgreeCheckboxes() {
      const checkboxes = [];

      // Handle single string ID case
      if (typeof this.agreeCheckboxId === "string") {
        const checkbox = document.getElementById(this.agreeCheckboxId);
        if (checkbox) checkboxes.push(checkbox);
        return checkboxes;
      }

      // Handle array of IDs case
      if (Array.isArray(this.agreeCheckboxId)) {
        this.agreeCheckboxId.forEach((id) => {
          const checkbox = document.getElementById(id);
          if (checkbox) checkboxes.push(checkbox);
        });
      }

      return checkboxes;
    },

    /**
     * Set up grouped inputs (checkboxes and radio buttons)
     * Handles inputs that share a common data group name
     */
    setupGroupedInputs() {
      // Handle checkbox groups
      const checkboxGroups = new Set();
      document
        .querySelectorAll('input[type="checkbox"][data-group]')
        .forEach((checkbox) => {
          checkboxGroups.add(checkbox.getAttribute("data-group"));
        });

      // Setup each checkbox group
      checkboxGroups.forEach((groupName) => {
        // Initialize group array if not exists
        if (!this.formData[groupName]) {
          this.formData[groupName] = [];
        }

        // Set initial checkbox states
        const groupCheckboxes = document.querySelectorAll(
          `input[type="checkbox"][data-group="${groupName}"]`,
        );
        groupCheckboxes.forEach((checkbox) => {
          if (this.formData[groupName].includes(checkbox.value)) {
            checkbox.checked = true;
          }

          // Add change event listener
          checkbox.addEventListener("change", () => {
            this.updateGroupValues(groupName, "checkbox");
          });
        });
      });

      // Handle radio groups
      const radioGroups = new Set();
      document
        .querySelectorAll('input[type="radio"][data-group]')
        .forEach((radio) => {
          radioGroups.add(radio.getAttribute("data-group"));
        });

      // Setup each radio group
      radioGroups.forEach((groupName) => {
        // Set initial radio selection
        if (this.formData[groupName]) {
          const selectedRadio = document.querySelector(
            `input[type="radio"][data-group="${groupName}"][value="${this.formData[groupName]}"]`,
          );
          if (selectedRadio) {
            selectedRadio.checked = true;
          }
        }

        // Add change event listeners
        const groupRadios = document.querySelectorAll(
          `input[type="radio"][data-group="${groupName}"]`,
        );
        groupRadios.forEach((radio) => {
          radio.addEventListener("change", () => {
            this.updateGroupValues(groupName, "radio");
          });
        });
      });
    },

    /**
     * Update values for grouped inputs (checkboxes or radios)
     *
     * @param {string} groupName - The data-group attribute value
     * @param {string} type - The input type ('checkbox' or 'radio')
     */
    updateGroupValues(groupName, type) {
      if (type === "checkbox") {
        const checkedBoxes = document.querySelectorAll(
          `input[type="checkbox"][data-group="${groupName}"]:checked`,
        );
        this.formData[groupName] = Array.from(checkedBoxes).map(
          (cb) => cb.value,
        );
      } else if (type === "radio") {
        const selectedRadio = document.querySelector(
          `input[type="radio"][data-group="${groupName}"]:checked`,
        );
        this.formData[groupName] = selectedRadio ? selectedRadio.value : null;
      }
      this.saveFormData();
    },

    /**
     * Handle file uploads
     * Reads the file as a data URL and stores it in formData
     *
     * @param {Event} event - The file input change event
     * @param {string} fieldName - The name of the file field
     */
    handleFileUpload(event, fieldName) {
      const file = event.target.files[0];
      if (!file) return;

      // Check file size
      const maxSize = this.maxFileSize * 1024 * 1024; // Convert to bytes
      if (file.size > maxSize) {
        alert(`File is too large. Maximum size is ${this.maxFileSize}MB.`);
        event.target.value = ""; // Clear the input
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        this.formData[fieldName] = e.target.result;
        this.saveFormData();
      };

      reader.onerror = (error) => {
        console.error("Error reading file:", error);
        alert("Error reading file. Please try again.");
      };

      // Read the file as a data URL (base64)
      reader.readAsDataURL(file);
    },

    /**
     * Remove a file that was previously uploaded
     *
     * @param {string} fieldName - The name of the file field
     */
    removeFile(fieldName) {
      delete this.formData[fieldName];

      // Clear the file input
      const fileInput = document.querySelector(`input[name="${fieldName}"]`);
      if (fileInput) {
        fileInput.value = "";
      }

      this.saveFormData();
    },

    /**
     * Handle hash change for navigation
     * Updates the current section based on URL hash
     *
     * @returns {boolean} True if hash navigation was successful
     */
    handleHashChange() {
      const hash = window.location.hash;
      if (hash && hash.startsWith("#")) {
        const sectionId = hash.substring(1);
        if (this.sectionExists(sectionId)) {
          this.goToSection(sectionId);
          return true;
        }
      }

      // If no valid hash or no hash at all, set the hash to the current section
      if (this.currentSection) {
        history.replaceState(null, "", `#${this.currentSection}`);
      }

      return false;
    },

    /**
     * Check if a section exists by ID
     *
     * @param {string} sectionId - The ID of the section to check
     * @returns {boolean} True if the section exists
     */
    sectionExists(sectionId) {
      return this.sections.some((section) => section.id === sectionId);
    },

    /**
     * Update section display
     * Shows the current section and hides others
     */
    updateSectionDisplay() {
      // Hide all sections
      this.sections.forEach((section) => {
        section.element.classList.remove("active");
      });

      // Show current section
      const currentSectionObj = this.getCurrentSection();
      if (currentSectionObj && currentSectionObj.element) {
        currentSectionObj.element.classList.add("active");
      }

      // Update URL hash without triggering hashchange event
      if (window.location.hash !== `#${this.currentSection}`) {
        history.replaceState(null, "", `#${this.currentSection}`);
      }
    },

    /**
     * Get current section object
     *
     * @returns {Object} The current section object
     */
    getCurrentSection() {
      return (
        this.sections.find((section) => section.id === this.currentSection) ||
        {}
      );
    },

    /**
     * Get current section index
     *
     * @returns {number} The index of the current section
     */
    getCurrentSectionIndex() {
      return this.sections.findIndex(
        (section) => section.id === this.currentSection,
      );
    },

    /**
     * Check if current section is the first one
     *
     * @returns {boolean} True if current section is the first section
     */
    isFirstSection() {
      return this.getCurrentSectionIndex() === 0;
    },

    /**
     * Check if current section is the last one
     *
     * @returns {boolean} True if current section is the last section
     */
    isLastSection() {
      return this.getCurrentSectionIndex() === this.sections.length - 1;
    },

    /**
     * Navigate to the next section
     * Validates the current section before proceeding
     */
    nextSection() {
      // Validate current section
      if (!this.validateSection(this.currentSection)) {
        return;
      }

      const currentIndex = this.getCurrentSectionIndex();
      if (currentIndex < this.sections.length - 1) {
        const nextSection = this.sections[currentIndex + 1];
        this.goToSection(nextSection.id);
      }
    },

    /**
     * Navigate to the previous section
     */
    prevSection() {
      const currentIndex = this.getCurrentSectionIndex();
      if (currentIndex > 0) {
        const prevSection = this.sections[currentIndex - 1];
        this.goToSection(prevSection.id);
      }
    },

    /**
     * Navigate to a specific section
     *
     * @param {string} sectionId - The ID of the section to navigate to
     */
    goToSection(sectionId) {
      // Don't navigate if section doesn't exist
      if (!this.sectionExists(sectionId)) {
        return;
      }

      // If trying to go forward, validate current section first
      const targetIndex = this.sections.findIndex(
        (section) => section.id === sectionId,
      );
      const currentIndex = this.getCurrentSectionIndex();

      if (
        targetIndex > currentIndex &&
        !this.validateSection(this.currentSection)
      ) {
        return;
      }

      // Update current section
      this.currentSection = sectionId;

      // Mark as visited
      if (!this.visitedSections.includes(sectionId)) {
        this.visitedSections.push(sectionId);
      }

      // Update display
      this.updateSectionDisplay();

      // Scroll to top
      window.scrollTo(0, 0);
    },

    /**
     * Validate a section
     * Checks required fields and sets error messages
     *
     * @param {string} sectionId - The ID of the section to validate
     * @returns {boolean} True if the section is valid
     */
    validateSection(sectionId) {
      this.errors = {};
      let isValid = true;

      // Find the section element
      const section = this.sections.find((s) => s.id === sectionId);
      if (!section) return true;

      // Get required fields in this section
      const requiredFields = section.element.querySelectorAll("[required]");

      requiredFields.forEach((field) => {
        if (!field.value.trim()) {
          this.errors[field.name] = "This field is required";
          isValid = false;
        }
      });

      return isValid;
    },

    /**
     * Check if a field has an error
     *
     * @param {string} fieldName - The name of the field to check
     * @returns {boolean} True if the field has an error
     */
    hasError(fieldName) {
      return this.errors[fieldName] !== undefined;
    },

    /**
     * Get error message for a field
     *
     * @param {string} fieldName - The name of the field
     * @returns {string} The error message or empty string
     */
    getError(fieldName) {
      return this.errors[fieldName] || "";
    },

    /**
     * Clear error for a field
     *
     * @param {string} fieldName - The name of the field
     */
    clearError(fieldName) {
      if (this.errors[fieldName]) {
        delete this.errors[fieldName];
      }
    },

    /**
     * Check if user has agreed to all required terms
     * Returns true if no agreement checkboxes exist
     *
     * @returns {boolean} True if all checkboxes are checked or no agreement required
     */
    isAgreed() {
      // If no agreement checkboxes exist, return true
      if (!this.agreeCheckboxExists()) {
        return true;
      }

      // Get all agreement checkboxes
      const agreeCheckboxes = this.getAgreeCheckboxes();

      // Check if all checkboxes are checked
      return agreeCheckboxes.every((checkbox) => {
        // Use the checkbox ID as the key in formData
        return this.formData[checkbox.id] === true;
      });
    },

    /**
     * Save form data to localStorage
     * Persists the current form state and data
     */
    saveFormData() {
      try {
        siteStorage.set(
          `${this.storagePrefix}Data`,
          JSON.stringify(this.formData),
        );
        siteStorage.set(
          `${this.storagePrefix}State`,
          JSON.stringify({
            currentSection: this.currentSection,
            visitedSections: this.visitedSections,
          }),
        );
      } catch (error) {
        console.error("Error saving form data:", error);
      }
    },

    /**
     * Load form data from localStorage
     * Restores previously saved form state and data
     */
    loadFormData() {
      try {
        // Load form data
        const savedData = siteStorage.get(`${this.storagePrefix}Data`);
        if (savedData) {
          this.formData = JSON.parse(savedData);
        }

        // Load form state
        const savedState = siteStorage.get(`${this.storagePrefix}State`);
        if (savedState) {
          const state = JSON.parse(savedState);

          if (
            state.currentSection &&
            this.sectionExists(state.currentSection)
          ) {
            this.currentSection = state.currentSection;
          }

          if (Array.isArray(state.visitedSections)) {
            this.visitedSections = state.visitedSections.filter((id) =>
              this.sectionExists(id),
            );
          }
        }
      } catch (error) {
        console.error("Error loading saved data:", error);
        // Reset if error
        this.formData = {};
        if (this.sections.length > 0) {
          this.currentSection = this.sections[0].id;
          this.visitedSections = [this.currentSection];
        }
      }
    },

    /**
     * Submit the form via HTTP POST to the configured endpoint
     *
     * @returns {Promise} Promise that resolves when submission is complete
     */
    async submitFormToEndpoint() {
      try {
        const response = await fetch(this.submitEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(this.formData),
        });

        if (!response.ok) {
          throw new Error(
            `Server returned ${response.status}: ${response.statusText}`,
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error submitting form:", error);
        throw error; // Re-throw to handle in calling function
      }
    },

    /**
     * Submit the form
     * Validates the final section and handles form submission
     */
    submitForm() {
      // Validate the final section
      if (!this.validateSection(this.currentSection)) {
        return;
      }

      // Check if user has agreed to all required terms
      if (this.agreeCheckboxExists() && !this.isAgreed()) {
        // Add error for each unchecked agreement checkbox
        const agreeCheckboxes = this.getAgreeCheckboxes();
        agreeCheckboxes.forEach((checkbox) => {
          if (!this.formData[checkbox.id]) {
            this.errors[checkbox.id] =
              "You must agree to the terms to continue";
          }
        });
        return;
      }

      // Show loading state
      this.isLoading = true;

      // Log the form data to console
      console.log(
        "Form data submitted:",
        JSON.stringify(this.formData, null, 2),
      );

      // Call onSubmit callback if provided
      if (typeof config.onSubmit === "function") {
        const result = config.onSubmit(this.formData);

        // If the callback returns a promise, handle it
        if (result instanceof Promise) {
          result
            .then(() => {
              this.isLoading = false;
              this.formSubmitted = true;
              // Update success message DOM element with x-cloak
              const successMessage = document.querySelector(
                ".form-success-message",
              );
              if (successMessage) {
                successMessage.removeAttribute("x-cloak");
              }
              this.clearStoredData();
            })
            .catch((error) => {
              this.isLoading = false;
              alert(
                "Error submitting form: " + (error.message || "Unknown error"),
              );
            });
          return;
        }
      } else {
        // Use the configured endpoint for submission
        this.submitFormToEndpoint()
          .then(() => {
            this.isLoading = false;
            this.formSubmitted = true;
            this.clearStoredData();
          })
          .catch((error) => {
            this.isLoading = false;
            alert(
              "Error submitting form: " + (error.message || "Unknown error"),
            );
          });
        return;
      }

      // For demo purposes or if no callback provided and submitFormToEndpoint fails, simulate API call with a delay
      setTimeout(() => {
        this.isLoading = false;
        this.formSubmitted = true;
        this.clearStoredData();
      }, this.submitDelay);
    },

    /**
     * Clear stored data from localStorage
     */
    clearStoredData() {
      siteStorage.remove(`${this.storagePrefix}Data`);
      siteStorage.remove(`${this.storagePrefix}State`);
    },

    /**
     * Reset the form
     * Clears all form data and returns to the first section
     */
    resetForm() {
      // Reset form element
      const form = document.getElementById(this.formId);
      if (form) {
        form.reset();
      }

      // Clear form data
      this.formData = {};

      // Reset form state
      if (this.sections.length > 0) {
        this.currentSection = this.sections[0].id;
        this.visitedSections = [this.currentSection];
      }

      this.formSubmitted = false;
      this.errors = {};

      // Update display
      this.updateSectionDisplay();

      // Clear localStorage
      this.clearStoredData();
    },
  }));
});
